/**
 * Generic event emitter for TypeScript applications
 * Provides a type-safe event emitter pattern similar to Node.js EventEmitter
 */

export interface BaseEvent {
  type: string;
}

export type EventListener<T = any> = (event: T) => void | Promise<void>;

/**
 * Generic EventEmitter class that provides type-safe event handling
 * Can be extended for specific event types
 */
export class EventEmitter<TEvent extends BaseEvent = BaseEvent> {
  private listeners = new Map<string, Set<EventListener<any>>>();
  private onceListeners = new Map<string, Set<EventListener<any>>>();

  /**
   * Register an event listener for a specific event type
   */
  on<T extends TEvent>(eventType: T["type"], listener: EventListener<T>): this {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(listener);
    return this;
  }

  /**
   * Register a one-time event listener that will be removed after first execution
   */
  once<T extends TEvent>(
    eventType: T["type"],
    listener: EventListener<T>,
  ): this {
    if (!this.onceListeners.has(eventType)) {
      this.onceListeners.set(eventType, new Set());
    }
    this.onceListeners.get(eventType)!.add(listener);
    return this;
  }

  /**
   * Remove a specific event listener
   */
  off<T extends TEvent>(
    eventType: T["type"],
    listener: EventListener<T>,
  ): this {
    this.listeners.get(eventType)?.delete(listener);
    this.onceListeners.get(eventType)?.delete(listener);
    return this;
  }

  /**
   * Emit an event to all registered listeners
   */
  emit<T extends TEvent>(event: T): this {
    const regularListeners = this.listeners.get(event.type);
    const onceListeners = this.onceListeners.get(event.type);

    // Execute once listeners and remove them
    if (onceListeners) {
      const listenersToExecute = Array.from(onceListeners);
      this.onceListeners.delete(event.type);

      for (const listener of listenersToExecute) {
        this.executeListener(listener, event);
      }
    }

    // Execute regular listeners
    if (regularListeners) {
      for (const listener of regularListeners) {
        this.executeListener(listener, event);
      }
    }

    return this;
  }

  /**
   * Remove all listeners for a specific event type, or all listeners if no type provided
   */
  removeAllListeners(eventType?: string): this {
    if (eventType) {
      this.listeners.delete(eventType);
      this.onceListeners.delete(eventType);
    } else {
      this.listeners.clear();
      this.onceListeners.clear();
    }
    return this;
  }

  /**
   * Get the count of listeners for a specific event type
   */
  listenerCount(eventType: string): number {
    const regular = this.listeners.get(eventType)?.size ?? 0;
    const once = this.onceListeners.get(eventType)?.size ?? 0;
    return regular + once;
  }

  /**
   * Get all registered event types
   */
  eventNames(): string[] {
    const names = new Set<string>();
    for (const key of this.listeners.keys()) {
      names.add(key);
    }
    for (const key of this.onceListeners.keys()) {
      names.add(key);
    }
    return Array.from(names);
  }

  /**
   * Execute a listener with error handling
   */
  private executeListener<T extends TEvent>(
    listener: EventListener<T>,
    event: T,
  ): void {
    try {
      const result = listener(event);
      // Handle async listeners
      if (result && typeof result.catch === "function") {
        result.catch((error: Error) => {
          this.handleListenerError(error, event);
        });
      }
    } catch (error) {
      this.handleListenerError(error as Error, event);
    }
  }

  /**
   * Handle errors from listeners - can be overridden in subclasses
   */
  protected handleListenerError(error: Error, event: TEvent): void {
    console.error(`Error in event listener for ${event.type}:`, error);
  }
}
